<template>
  <div class="mod-dashboard">
    <!-- 顶部标题栏 -->
    <div class="dashboard-header">
      <span class="dashboard-header__title">数据概览</span>
    </div>

    <div class="dashboard-body">
      <!-- 指标卡片 -->
      <el-row :gutter="20" v-loading="statLoading">
        <el-col :xs="24" :sm="12" :lg="6" v-for="item in statCards" :key="item.key">
          <div class="stat-card">
            <div class="stat-card__label">{{ item.label }}</div>
            <div class="stat-card__value">{{ item.value }}</div>
            <div class="stat-card__trend" :class="trendClass(item)">
              <template v-if="item.rate !== null">
                <i :class="item.rate >= 0 ? 'el-icon-top' : 'el-icon-bottom'"></i>
                {{ Math.abs(item.rate).toFixed(1) }}% {{ item.compare }}
              </template>
              <span v-else class="stat-card__trend--none">{{ item.compare }}</span>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 图表区 -->
      <el-row :gutter="20" class="dashboard-charts">
        <el-col :xs="24" :lg="16">
          <div class="panel">
            <div class="panel__header">
              近30天收入趋势
              <el-tag size="mini" type="warning">示例数据</el-tag>
            </div>
            <div class="panel__body">
              <div ref="revenueChart" class="chart chart--bar"></div>
            </div>
          </div>
        </el-col>
        <el-col :xs="24" :lg="8">
          <div class="panel">
            <div class="panel__header">
              套餐销售占比
              <el-tag size="mini" type="warning">示例数据</el-tag>
            </div>
            <div class="panel__body">
              <div ref="packageChart" class="chart chart--pie"></div>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 热门标签 -->
      <div class="panel dashboard-tags">
        <div class="panel__header">
          热门标签 TOP10
          <el-tag size="mini" type="warning">点击量 / 转化率为示例数据</el-tag>
        </div>
        <div class="panel__body">
          <el-table :data="hotTags" v-loading="tagLoading" style="width: 100%;">
            <el-table-column type="index" label="排名" width="90"></el-table-column>
            <el-table-column prop="name" label="标签"></el-table-column>
            <el-table-column prop="videos" label="关联视频">
              <template slot-scope="scope">{{ formatNumber(scope.row.videos) }}</template>
            </el-table-column>
            <el-table-column prop="clicks" label="点击量">
              <template slot-scope="scope">{{ formatNumber(scope.row.clicks) }}</template>
            </el-table-column>
            <el-table-column prop="rate" label="转化率"></el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  getUserPage,
  getPayOrderPage,
  getHotTags,
  getRevenueTrendMock,
  getPackageRatioMock,
  mockTagMetrics
} from '@/api/dashboard'

// 支付状态：1=支付成功
const PAY_STATUS_SUCCESS = '1'

export default {
  name: 'Dashboard',
  data () {
    return {
      statLoading: false,
      tagLoading: false,
      stat: {
        todayUser: 0,
        yesterdayUser: 0,
        todayOrder: 0,
        yesterdayOrder: 0,
        todayAmount: 0,
        yesterdayAmount: 0,
        vipCount: 0
      },
      hotTags: [],
      revenueChart: null,
      packageChart: null
    }
  },

  computed: {
    statCards () {
      const { stat } = this
      return [
        {
          key: 'user',
          label: '今日新增用户',
          value: this.formatNumber(stat.todayUser),
          rate: this.calcRate(stat.todayUser, stat.yesterdayUser),
          compare: '较昨日'
        },
        {
          key: 'order',
          label: '今日订单数',
          value: this.formatNumber(stat.todayOrder),
          rate: this.calcRate(stat.todayOrder, stat.yesterdayOrder),
          compare: '较昨日'
        },
        {
          key: 'amount',
          label: '今日收入 (元)',
          value: this.formatNumber(stat.todayAmount),
          rate: this.calcRate(stat.todayAmount, stat.yesterdayAmount),
          compare: '较昨日'
        },
        {
          key: 'vip',
          label: '活跃会员数',
          value: this.formatNumber(stat.vipCount),
          rate: null,
          compare: 'VIP 用户总数'
        }
      ]
    }
  },

  mounted () {
    this.loadStat()
    this.loadHotTags()
    this.$nextTick(() => {
      this.initCharts()
    })
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    if (this.revenueChart) {
      this.revenueChart.dispose()
      this.revenueChart = null
    }
    if (this.packageChart) {
      this.packageChart.dispose()
      this.packageChart = null
    }
  },

  methods: {
    // ---------- 指标卡片（真实接口） ----------
    async loadStat () {
      this.statLoading = true
      const today = this.dayRange(0)
      const yesterday = this.dayRange(-1)
      // 单个接口失败不影响其它指标展示
      const safe = promise => promise.catch(e => {
        console.error('加载概览指标失败:', e)
        return null
      })

      const [todayUser, yesterdayUser, todayOrder, yesterdayOrder, vip] = await Promise.all([
        safe(getUserPage({ page: 1, limit: 1, startDate: today[0], endDate: today[1] })),
        safe(getUserPage({ page: 1, limit: 1, startDate: yesterday[0], endDate: yesterday[1] })),
        safe(getPayOrderPage({ page: 1, limit: 1, orderType: PAY_STATUS_SUCCESS, startDate: today[0], endDate: today[1] })),
        safe(getPayOrderPage({ page: 1, limit: 1, orderType: PAY_STATUS_SUCCESS, startDate: yesterday[0], endDate: yesterday[1] })),
        safe(getUserPage({ page: 1, limit: 1, memberVip: '1' }))
      ])

      this.stat.todayUser = this.pickUserTotal(todayUser)
      this.stat.yesterdayUser = this.pickUserTotal(yesterdayUser)
      this.stat.vipCount = this.pickUserTotal(vip)

      const todayOrderData = this.pickOrderData(todayOrder)
      const yesterdayOrderData = this.pickOrderData(yesterdayOrder)
      this.stat.todayOrder = todayOrderData.total
      this.stat.todayAmount = todayOrderData.amount
      this.stat.yesterdayOrder = yesterdayOrderData.total
      this.stat.yesterdayAmount = yesterdayOrderData.amount

      this.statLoading = false
    },

    // /user/user/page 返回 { code, data: { list, total } }
    pickUserTotal (res) {
      const body = res && res.data
      if (!body || body.code !== 0 || !body.data) return 0
      return body.data.total || 0
    },

    // /payOrder/payorder/page 返回 { code, data: { pageData: { total }, totalRechargeAmount } }
    pickOrderData (res) {
      const body = res && res.data
      if (!body || body.code !== 0 || !body.data) return { total: 0, amount: 0 }
      return {
        total: (body.data.pageData && body.data.pageData.total) || 0,
        amount: Number(body.data.totalRechargeAmount || 0)
      }
    },

    // ---------- 热门标签（名称、关联视频数为真实数据；点击量、转化率无来源） ----------
    async loadHotTags () {
      this.tagLoading = true
      try {
        const { data: res } = await getHotTags(10)
        if (res.code !== 0) {
          this.hotTags = []
          return
        }
        const list = (res.data && res.data.list) || []
        this.hotTags = list.map(item => ({
          name: item.name,
          videos: item.movieCount || 0,
          ...mockTagMetrics(item.id)
        }))
      } catch (e) {
        console.error('加载热门标签失败:', e)
        this.hotTags = []
      } finally {
        this.tagLoading = false
      }
    },

    // ---------- 图表（假数据） ----------
    initCharts () {
      this.renderRevenueChart()
      this.renderPackageChart()
    },

    renderRevenueChart () {
      if (!this.$refs.revenueChart) return
      this.revenueChart = echarts.init(this.$refs.revenueChart)
      const { dates, values } = getRevenueTrendMock(30)
      this.revenueChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          valueFormatter: value => `${this.formatNumber(value)} 元`
        },
        grid: { left: 10, right: 20, top: 30, bottom: 10, containLabel: true },
        xAxis: {
          type: 'category',
          data: dates,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e4e7ed' } },
          axisLabel: { color: '#909399', interval: 2 }
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: '#909399' },
          splitLine: { lineStyle: { color: '#f0f2f5' } }
        },
        series: [{
          type: 'bar',
          data: values,
          barMaxWidth: 26,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#4a7cf7' },
              { offset: 1, color: '#c3d6fd' }
            ])
          }
        }]
      })
    },

    renderPackageChart () {
      if (!this.$refs.packageChart) return
      this.packageChart = echarts.init(this.$refs.packageChart)
      const data = getPackageRatioMock()
      this.packageChart.setOption({
        color: ['#3b6ef3', '#7c3aed', '#ec4899', '#f59e0b', '#22c55e'],
        tooltip: { trigger: 'item', formatter: '{b}: {c}%' },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          icon: 'circle',
          itemGap: 16,
          textStyle: { color: '#606266' },
          formatter: name => {
            const target = data.find(item => item.name === name)
            return target ? `${name} ${target.value}%` : name
          }
        },
        series: [{
          type: 'pie',
          radius: ['45%', '70%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          data
        }]
      })
    },

    handleResize () {
      this.revenueChart && this.revenueChart.resize()
      this.packageChart && this.packageChart.resize()
    },

    // ---------- 工具方法 ----------
    // 返回指定偏移天数的 [开始时间, 结束时间]，格式 yyyy-MM-dd HH:mm:ss
    dayRange (offset) {
      const date = new Date()
      date.setDate(date.getDate() + offset)
      const day = this.formatDate(date)
      return [`${day} 00:00:00`, `${day} 23:59:59`]
    },

    formatDate (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    formatNumber (value) {
      const num = Number(value || 0)
      if (Number.isNaN(num)) return '0'
      const fixed = Number.isInteger(num) ? String(num) : num.toFixed(2)
      const [integer, decimal] = fixed.split('.')
      const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return decimal ? `${formatted}.${decimal}` : formatted
    },

    // 环比，基数为 0 时返回 null（不展示箭头）
    calcRate (current, previous) {
      if (!previous) return null
      return ((current - previous) / previous) * 100
    },

    trendClass (item) {
      if (item.rate === null) return ''
      return item.rate >= 0 ? 'is-up' : 'is-down'
    }
  }
}
</script>

<style lang="scss" scoped>
.mod-dashboard {
  background-color: #f5f7fa;
  min-height: 100%;
}

.dashboard-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fff;
  border-bottom: 1px solid #ebeef5;

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }
}

.dashboard-body {
  padding: 16px;
}

.stat-card {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;

  &__label {
    font-size: 14px;
    color: #909399;
  }

  &__value {
    font-size: 24px;
    font-weight: 600;
    color: #1f2d3d;
    line-height: 1.4;
    margin: 8px 0;
  }

  &__trend {
    font-size: 13px;
    color: #909399;

    &.is-up {
      color: #67c23a;
    }

    &.is-down {
      color: #f56c6c;
    }

    &--none {
      color: #909399;
    }
  }
}

.panel {
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 20px;

  &__header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
    font-size: 14px;
    font-weight: 600;
    color: #1f2d3d;

    .el-tag {
      margin-left: 10px;
      font-weight: 400;
    }
  }

  &__body {
    padding: 16px;
  }
}

.chart {
  width: 100%;

  &--bar {
    height: 340px;
  }

  &--pie {
    height: 340px;
  }
}

.dashboard-tags {
  margin-bottom: 0;

  ::v-deep .el-table th {
    background-color: #fafbfc;
    color: #909399;
    font-weight: 500;
  }
}
</style>
